import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { intercompanySelectors } from 'store/reducers/intercompanySlice';
import {
    populateMissingLpns,
    returnSelectors,
    returnThunks,
    selectAllM3ItemsHaveLpn,
    setBatchHandled,
} from 'store/reducers/returnSlice';
import { ReturnItemRowStatus, ReturnSlip, ReturnStatusType } from 'returnCenter-api';
import { selectOrigin, setOrigin, setSelectedStatus } from 'store/reducers/navigationSlice';
import { getStatusTypeString } from 'utils/helpers';
import { configSelectors } from 'store/reducers/configSlice';

interface UseReturnSlipActionsOptions {
    returnSlip: ReturnSlip | null | undefined;
    returnSlipId: number | null | undefined;
    orderIdFromPath: string | undefined;
    returnStatus: ReturnStatusType | undefined;
}

export const useReturnSlipActions = ({
    returnSlip,
    returnSlipId,
    orderIdFromPath,
    returnStatus,
}: UseReturnSlipActionsOptions) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const origin = useAppSelector(selectOrigin);
    const latestReturnSlip = useAppSelector(returnSelectors.selectReturnSlip);
    const allItemsHaveReasonCodes = useAppSelector(returnSelectors.selectValidityCheckForReasonCode);
    const allItemsHaveDecisionCodes = useAppSelector(returnSelectors.selectValidityCheckForDecisionCode);
    const selectedReturnItemsHaveQty = useAppSelector(returnSelectors.selectIsReturnItemsQtyValid);
    const eachOrderHasReturnItemsWithQty = useAppSelector(returnSelectors.selectHasEachOrderAtLeastOneItemWithQty);
    const isPublicReturn = useAppSelector(returnSelectors.selectIsPublicReturn);
    const intercompanyProblems = useAppSelector(intercompanySelectors.selectProblems);
    const isM3Company = useAppSelector(configSelectors.selectIsM3Company);
    const allM3ItemsHaveLpn = useAppSelector(selectAllM3ItemsHaveLpn);

    const navigateToReturnSlip = useCallback(
        (slipId: number, status: ReturnStatusType) => {
            const statusKey = getStatusTypeString(status);
            dispatch(setSelectedStatus(status));
            dispatch(setOrigin(origin ?? null));
            navigate(`/returns/${statusKey}/${slipId}`, {
                state: {
                    origin: origin ?? null,
                    returnStatus: status,
                },
            });
        },
        [navigate, dispatch, origin],
    );

    const updateReturnSlip = useCallback(
        (status?: ReturnStatusType) => {
            if (!latestReturnSlip) return;
            const updatedSlip = { ...latestReturnSlip, status: status ?? latestReturnSlip.status };
            dispatch(
                returnThunks.updateReturnSlip({
                    slip: updatedSlip,
                }),
            ).then((action) => {
                if (returnThunks.updateReturnSlip.fulfilled.match(action)) {
                    const updatedSlip = action.payload;
                    if (!updatedSlip?.id) return;

                    if (isM3Company) {
                        dispatch(populateMissingLpns());
                    }

                    navigateToReturnSlip(updatedSlip.id, updatedSlip.status ?? status ?? ReturnStatusType.InProgress);
                }
            });
        },
        [dispatch, latestReturnSlip, navigateToReturnSlip],
    );

    const createReturnSlip = useCallback(
        (status?: ReturnStatusType) => {
            if (!returnSlip) return;

            return dispatch(
                returnThunks.createReturnSlip({
                    slip: returnSlip,
                    returnStatus: status,
                }),
            ).then((action) => {
                if (returnThunks.createReturnSlip.fulfilled.match(action)) {
                    const createdSlip = action.payload;
                    if (!createdSlip?.id) return;

                    navigateToReturnSlip(createdSlip.id, ReturnStatusType.InProgress);
                }
            });
        },
        [dispatch, returnSlip, navigateToReturnSlip],
    );

    const handleSaveWithStatus = useCallback(
        (status: ReturnStatusType) => {
            if (!returnSlip) return;

            if (!allItemsHaveReasonCodes) {
                toast.error(t('allItemsMustHaveReasonCodes'), {
                    autoClose: 3500,
                    closeOnClick: true,
                });
                return;
            }

            if (!eachOrderHasReturnItemsWithQty) {
                toast.error(t('eachOrderMustHaveAtLeastOneItemWithQty'), { autoClose: 3500, closeOnClick: true });
                return;
            }

            if (!selectedReturnItemsHaveQty) {
                toast.error(t('invalidQuantityInReturn'), { autoClose: 3500, closeOnClick: true });
                return;
            }

            if (returnSlipId) {
                updateReturnSlip(status);
                return;
            }

            createReturnSlip(status);
        },
        [
            returnSlip,
            returnSlipId,
            orderIdFromPath,
            allItemsHaveReasonCodes,
            selectedReturnItemsHaveQty,
            eachOrderHasReturnItemsWithQty,
            t,
            createReturnSlip,
            updateReturnSlip,
        ],
    );

    // Validation function for complete return - call this before showing dialog
    const validateCompleteReturn = useCallback((): boolean => {
        if (!allItemsHaveReasonCodes) {
            toast.error(t('allItemsMustHaveReasonCodes'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return false;
        }

        if (isM3Company && !allM3ItemsHaveLpn) {
            toast.error(t('allItemsMustHaveLpn', 'All returned items must have an LPN.'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return false;
        }

        if (!eachOrderHasReturnItemsWithQty) {
            toast.error(t('eachOrderMustHaveAtLeastOneItemWithQty'), { autoClose: 3500, closeOnClick: true });
            return false;
        }

        if (!allItemsHaveDecisionCodes) {
            toast.error(t('allItemsMustHaveDecisionCodes'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return false;
        }

        // Public return: all rows with quantityRequested must be approved
        if (isPublicReturn && returnSlip) {
            const hasPendingRows = returnSlip.returnItems
                ?.flatMap((group) => group.orderLines ?? [])
                .flatMap((ol) => ol.returnItems ?? [])
                .some((item) => (item.quantityRequested ?? 0) > 0 && item.status !== ReturnItemRowStatus.Approved);

            if (hasPendingRows) {
                toast.error(t('allRowsMustBeApproved'), {
                    autoClose: 3500,
                    closeOnClick: true,
                });
                return false;
            }
        }

        // An unlocked intercompany row is the operator's to decide, so leaving it empty is an
        // unfinished decision. Without this the panel would say "you choose" and then the supplying
        // company's rules would choose anyway, which is the one outcome nobody asked for.
        const undecidedIntercompanyRows = (returnSlip?.returnItems ?? [])
            .flatMap((group) => group.orderLines ?? [])
            .filter((line) => line.isIntercompanyEligible)
            .flatMap((line) => line.returnItems ?? [])
            .filter(
                (item) =>
                    (item.quantityInReturn ?? 0) > 0 &&
                    !item.batchReturnId &&
                    !item.intercompanyReturnId &&
                    item.intercompanyDecisionCodeIsManual &&
                    item.intercompanyDecisionCode == null,
            );

        if (undecidedIntercompanyRows.length > 0) {
            toast.error(
                t('intercompany.pickADecisionCode', {
                    count: undecidedIntercompanyRows.length,
                    defaultValue:
                        'Cannot complete: {{count}} unlocked intercompany line(s) have no decision code chosen.',
                }),
                { autoClose: 6000, closeOnClick: true },
            );
            return false;
        }

        // Completing also credits the supplying companies. A line they cannot credit would fail
        // after the customer is already credited, leaving someone to find it in the return log —
        // so stop here instead, while it can still be fixed.
        if (intercompanyProblems.length > 0) {
            const first = intercompanyProblems[0];
            toast.error(
                t('intercompany.cannotComplete', {
                    company: first.supplyingCompany,
                    sku: first.sku,
                    count: intercompanyProblems.length,
                    defaultValue:
                        'Cannot complete: {{count}} intercompany line(s) cannot be credited by {{company}}, starting with {{sku}}.',
                }),
                { autoClose: 6000, closeOnClick: true },
            );
            return false;
        }

        return true;
    }, [
        allItemsHaveReasonCodes,
        allItemsHaveDecisionCodes,
        allM3ItemsHaveLpn,
        eachOrderHasReturnItemsWithQty,
        intercompanyProblems,
        isM3Company,
        isPublicReturn,
        returnSlip,
        t,
    ]);

    const handleCompleteReturnSlip = useCallback(() => {
        if (!returnSlip) return;

        if (!validateCompleteReturn()) {
            return;
        }

        const returnIdSlip = returnSlip?.id;

        dispatch(
            returnThunks.createReturnCredit({
                slip: returnSlip,
                returnId: returnIdSlip,
                returnStatus,
            }),
        ).then((action) => {
            if (returnThunks.createReturnCredit.fulfilled.match(action)) {
                const createdSlip = action.payload;
                if (!createdSlip?.id) return;
                navigateToReturnSlip(createdSlip.id, createdSlip.status ?? ReturnStatusType.Completed);
            }
        });
    }, [dispatch, navigate, returnSlip, returnStatus]);

    const handleCompleteBatch = useCallback(() => {
        if (!latestReturnSlip) return;
        const updatedSlip = { ...latestReturnSlip, batchHandled: true, status: ReturnStatusType.Completed };
        dispatch(
            returnThunks.updateReturnSlip({
                slip: updatedSlip,
            }),
        ).then((action) => {
            if (returnThunks.updateReturnSlip.fulfilled.match(action)) {
                const result = action.payload;
                if (!result?.id) return;
                navigateToReturnSlip(result.id, result.status ?? ReturnStatusType.Completed);
            }
        });
    }, [dispatch, latestReturnSlip, navigateToReturnSlip]);

    const handleApproveBatchHandling = useCallback(() => {
        if (!latestReturnSlip) return;
        dispatch(setBatchHandled(true));
    }, [dispatch, latestReturnSlip]);

    const handleUnapproveBatchHandling = useCallback(() => {
        if (!latestReturnSlip) return;
        dispatch(setBatchHandled(false));
    }, [dispatch, latestReturnSlip]);

    const getUnassignedBatchItemCount = useCallback((): number => {
        if (!latestReturnSlip?.returnItems) return 0;
        let count = 0;
        for (const group of latestReturnSlip.returnItems) {
            for (const line of group.orderLines ?? []) {
                for (const item of line.returnItems ?? []) {
                    if (item.batchReturnDestinationId != null && !item.batchReturnId) {
                        count++;
                    }
                }
            }
        }
        return count;
    }, [latestReturnSlip]);

    return {
        handleSaveWithStatus,
        handleCompleteReturnSlip,
        handleCompleteBatch,
        handleApproveBatchHandling,
        handleUnapproveBatchHandling,
        getUnassignedBatchItemCount,
        validateCompleteReturn,
        updateReturnSlip,
        createReturnSlip,
    };
};

export default useReturnSlipActions;
